import {
  GAME_PLATFORM_LABELS,
  type GamePlatform,
  type PublishingDestination,
  type PublishingStore,
} from '@playerbase/types';

import { AddDestinationPopover } from './add-destination-popover.js';
import { DestinationRow } from './destination-row.js';

type PlatformSectionProps = {
  platform: GamePlatform;
  destinations: PublishingDestination[];
  onAddStore: (platform: GamePlatform, store: PublishingStore) => void | Promise<void>;
  onRemove: (destination: PublishingDestination) => void | Promise<void>;
  onUpdateUrl: (
    destination: PublishingDestination,
    storeUrl: string | null,
  ) => void | Promise<void>;
};

export function PlatformSection({
  platform,
  destinations,
  onAddStore,
  onRemove,
  onUpdateUrl,
}: PlatformSectionProps): JSX.Element {
  const existingStores = new Set(destinations.map((d) => d.store));
  return (
    <section className="rounded-sm border border-border bg-surface p-4">
      <header className="flex items-center justify-between">
        <h3 className="font-display text-base font-semibold text-text">
          {GAME_PLATFORM_LABELS[platform]}
        </h3>
        <AddDestinationPopover
          platform={platform}
          existingStores={existingStores}
          onAdd={(store) => onAddStore(platform, store)}
        />
      </header>

      {destinations.length === 0 ? (
        <p className="mt-3 text-xs text-text-muted">
          No publishing destinations yet. Add one to capture where this game ships on
          {' '}{GAME_PLATFORM_LABELS[platform]}.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {destinations.map((dest) => (
            <li key={dest.id}>
              <DestinationRow
                destination={dest}
                onRemove={() => onRemove(dest)}
                onUpdateUrl={(url) => onUpdateUrl(dest, url)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
